// src/pages/SignupPage.tsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { FirebaseError } from 'firebase/app';
import AuthLayout from './AuthLayout';
import {
  addUser,
  ifUserExists
} from "../services/firebase";
import { useAuth } from '../contexts/AuthContext';
import { sendSignUpVerificationEmail } from '../services/sendgrid-service';
import { set } from 'lodash';

const validateEmail = (email: string) =>
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

const validatePassword = (password: string) =>
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*]).{8,}$/.test(password);

const SignupPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [msg, setMsg] = useState('');
  const { login, getDashboardRoute, logout } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailError(validateEmail(e.target.value) ? '' : 'Invalid email address.');
    if (msg) setMsg('');
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setPasswordError(
      validatePassword(e.target.value)
        ? ''
        : 'Password must be at least 8 characters, include uppercase, lowercase, a number, and a special character.'
    );
    if (msg) setMsg('');
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg('');

    if (!validateEmail(email)) {
      setEmailError('Invalid email address.');
      return;
    }

    if (!validatePassword(password)) {
      setPasswordError(
        'Password must be at least 8 characters, include uppercase, lowercase, a number, and a special character.'
      );
      return;
    }

    try {
      setIsSubmitting(true);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const verifyToken = crypto.randomUUID(); // Generate a unique token for email verification
      const docID = await addUser({
        uid: user.uid,
        email: user.email ? user.email : '',
        role: "user",
        name: name,
        verified: false,
        verifyToken
      });
      await logout();
      if(!docID) {
        setIsSubmitting(false);
        setMsg('Failed to signup.');
        return;
      } else {
        setMsg('Signup successful! Please check your email to verify.');
        //sendEmailVerification
        setEmail('');
        setPassword('');
        setName('');
        setIsSubmitting(false);
        try {
          await sendSignUpVerificationEmail(email, name, `${window.location.origin}/verify?token=${verifyToken}`);
          setIsSubmitting(false);
        } catch (error) {
          console.log('Error sending verification email:', error);
          setIsSubmitting(false);
        }
        
      }


      /* await sendEmailVerification(user, {
        url: `${window.location.origin}/dashboard`,
      });

      await setDoc(doc(db, 'users', user.uid), {
        id: user.uid,
        email,
        role: 'user',
      });

      setMsg('Signup successful! Please check your email to verify.'); */
    } catch (error) {
      setIsSubmitting(false);
      const firebaseError = error as FirebaseError;
      if (firebaseError.code === 'auth/email-already-in-use') {
        setMsg('Email already registered. Try logging in.');
      } else {
        setMsg('Signup failed. Please try again.');
      }
    }
  };

  const isFormValid =
    name.trim() !== '' &&
    validateEmail(email) &&
    validatePassword(password);

  return (
    <AuthLayout title="Join the league where last place is legendary">
      <form onSubmit={handleSignup}>
        <div className="formGroup">
          <input
            type="text"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus={true}
          />
        </div>

        <div className="formGroup">
          <input
            type="email"
            placeholder="Email Address"
            value={email}
            onChange={handleEmailChange}
            required
          />
          {emailError && <div className="errorText">{emailError}</div>}
        </div>

        <div className="formGroup">
          <input
            type="password"
            placeholder="Create a Password"
            value={password}
            onChange={handlePasswordChange}
            required
          />
          {passwordError && <div className="errorText">{passwordError}</div>}
        </div>

        <button type="submit" className="btnStyle" disabled={!isFormValid || isSubmitting}>
          Continue
        </button>

        {msg && (
          <p
            style={{
              color: msg.includes('successful') ? 'green' : 'red',
              marginTop: '10px',
              textAlign: 'center',
              fontSize: '12px',
            }}
          >
            {msg}
          </p>
        )}

        <div className="alignCenter topGap1">
          Already have an account? <Link to="/login">Sign In</Link>
        </div>

        
      </form>
    </AuthLayout>
  );
};

export default SignupPage;
